import React, { useState } from 'react';
import { Modal, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import VisitModalContent from './VisitModalContent';
import AudioUploadSection from './AudioUploadSection';
import toast from 'react-hot-toast';
import { logEvent } from '../../analytics';
import startSession from '../../utils/session/startSession';
import SessionPresenter from '../../presenters/SessionPresenter';
import NoteTypeSelector from './NoteTypeSelector';
import confirmVisitDiscard from './VisitDiscardConfirm';
import uploadAudioFile from '../../utils/session/uploadAudioFile';
import updateSession from '../../utils/session/updateSession';

export default function AudioUploadModal({ open, onClose }) {
  const { t } = useTranslation();
  const [session, setSession] = useState(null);
  const [modalKey, setModalKey] = useState(0);
  const [isFileAttached, setIsFileAttached] = useState(false);
  const [showNoteTypeSelector, setShowNoteTypeSelector] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handleClose = async () => {
    if (!showNoteTypeSelector) {
      console.log('AudioUploadModal: handleClose - session:', session?.getUUID());
      
      if (session?.getUUID()) {
        const confirmed = await confirmVisitDiscard({ 
          sessionUuid: session.getUUID(), 
          t 
        });
        if (!confirmed) {
          return; // Don't close if user cancels
        }
      }
      
      setModalKey(prev => prev + 1); // Increment key to force remount
      setSession(null);
      setSelectedPatient(null);
      setIsFileAttached(false);
      onClose();
    }
  };

  const handleFileAttached = async (file) => {
    if (!file) {
      setIsFileAttached(false);
      return;
    }
    let currentSession = session;
    try {
      if (!currentSession) {
        const sessionUuid = await startSession();
        if (!sessionUuid) throw new Error('Failed to create session');
        currentSession = new SessionPresenter({ uuid: sessionUuid });
        setSession(currentSession);
      }
      await uploadAudioFile(currentSession.getUUID(), file);
      setIsFileAttached(true);
      logEvent('SessionRecorder', 'UploadAudio');
    } catch (error) {
      toast.error(t('session_recorder.errors.upload_failed'));
      setSession(null);
    }
    return false;
  };

  const handleFinalize = async () => {
    try {
      if (!session.getPatient() && selectedPatient) {
        const updatedSession = await updateSession(session.getUUID(), {
          patient_uuid: selectedPatient.getUUID(),
        });
        console.log('updatedSession', updatedSession);
        setSession(updatedSession);
      }
      setShowNoteTypeSelector(true);
    } catch (error) {
      console.error('Error finalizing session:', error);
      toast.error(t('common.errors.general'));
    }
  };

  const handleCloseNoteTypeSelector = () => {
    setShowNoteTypeSelector(false);
    setSession(null);
    setSelectedPatient(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={t('visit_audio_upload.title')}
        open={open && !showNoteTypeSelector}
        onCancel={handleClose}
        maskClosable={false}
        footer={isFileAttached ? (
          <div style={{ textAlign: 'center' }}>
            <Button 
              type="primary" 
              onClick={handleFinalize}
              size="large"
            >
              {t('visit_audio_upload.finalize_button')}
            </Button>
          </div>
        ) : null}
        width={800}
      >
        <VisitModalContent
          key={modalKey}
          sessionUuid={session?.getUUID()}
          onPatientChange={setSelectedPatient}
        >
          <AudioUploadSection 
            onFileAttached={handleFileAttached}
          />
        </VisitModalContent>
      </Modal>

      {showNoteTypeSelector && session && (
        <NoteTypeSelector
          sessionUuid={session?.getUUID()}
          visible={showNoteTypeSelector}
          onClose={handleCloseNoteTypeSelector}
          patient={selectedPatient}
        />
      )}
    </>
  );
} 